/**
 * @file LogObserver / ConsoleLogSink - Platform-agnostic log subscribers.
 *
 * **LogObserver** handles the common work of subscribing to a service tree's
 * 'log' events, extracting structured metadata (source, hierarchy, span
 * context), and building a well-typed LogEntry. Platform-specific output is
 * left to subclasses via `onEntry()`.
 *
 * **ConsoleLogSink** is the universal default implementation. It writes
 * structured `LogEntry` objects to the platform `console`. Works in all
 * JavaScript environments: browsers, Node.js, edge workers.
 *
 * On Node.js TTY it produces colorized multi-line output. On non-TTY Node.js
 * (piped output, CI) it emits compact JSON for log aggregators. In browsers
 * it routes to `console.error/warn/debug/log` with a structured detail object
 * that devtools render as an interactive tree.
 *
 * **Usage — custom sink**
 *
 * ```typescript
 * class MyLogSink extends LogObserver {
 *   protected onEntry(entry: LogEntry): void {
 *     console.log(JSON.stringify(entry))
 *   }
 * }
 *
 * const sink = new MyLogSink()
 * sink.attach(rootService)
 * ```
 *
 * **Usage — console sink (all platforms)**
 *
 * ```typescript
 * const sink = new ConsoleLogSink('debug')
 * sink.attach(parent)
 * ```
 *
 * **Level filtering**
 *
 * By default, `getMinLevel()` reads `CoreClass.globalLogLevel` on every event,
 * so runtime calls to `instance.setLogLevel()` are honoured
 * immediately. Pass a `minLevel` to `ConsoleLogSink` to pin to a fixed level.
 *
 * @module
 */

import { CoreClass } from './core_class.ts'
import { LOG_LEVEL_VALUES } from './core_telemetry.ts'
import type { LogEntry, LogLevel } from './core_telemetry.ts'
import { getCurrentSpanContext } from './span.ts'

/**
 * JSON.stringify that safely handles circular references by replacing any
 * object seen more than once in the traversal with the string "[Circular]".
 * Used throughout log output to prevent crashes when log context accidentally
 * captures objects with circular refs (e.g. NATS connection internals).
 */
function safeStringify(value: unknown, indent?: number): string {
  const seen = new Set<unknown>()
  return JSON.stringify(
    value,
    (_, v: unknown) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]'
        seen.add(v)
      }
      return v
    },
    indent,
  )
}

////////////////////////////////////////////////////////////////////////////////
// LogObserver
////////////////////////////////////////////////////////////////////////////////

/**
 * Abstract base for log observers.
 *
 * Attach to any node in the service tree with `attach()`. All log events
 * emitted by that node and its descendants are received, filtered, and
 * forwarded as `LogEntry` objects to `onEntry()`.
 */
export abstract class LogObserver {
  /**
   * Per-target listener registry. Holds the listener function `attach()`
   * registered with `target.on('log', …)` so `detach()` can pass the same
   * reference to `target.off('log', …)`. The key is weak so a detached
   * (or simply discarded) target doesn't keep the observer alive.
   */
  private readonly listeners = new WeakMap<
    CoreClass,
    (...args: any[]) => void
  >()

  /**
   * Subscribe to log events from `target` and all its descendants.
   *
   * Typically called in `onConfigure()` with `this.parent` as the target.
   * The subscriber is held strongly by the target's event set and lives as
   * long as the target — or until `detach(target)` removes it.
   *
   * Span context from the current call-stack (if any) is included in the
   * `LogEntry`. Backend `LogSink` overwrites this with the more accurate
   * `AsyncLocalStorage` context.
   *
   * @param target - The node whose 'log' events to observe.
   */
  attach(target: CoreClass): void {
    // Idempotent on the same target — a second `attach(target)` call is
    // a no-op. Without this guard, the `listeners` `WeakMap` entry from
    // the first call would be overwritten by the second, and a
    // subsequent `detach(target)` would only remove the *second*
    // registration, silently leaking the first listener.
    if (this.listeners.has(target)) return
    const listener = (
      level: string,
      event: string,
      context: object | undefined,
      ...hierarchy: CoreClass[]
    ) => {
      if (LOG_LEVEL_VALUES[level as LogLevel] < this.getMinLevel()) return

      const spanCtx = getCurrentSpanContext()

      // Hierarchy snapshot timing: each `h.toJSON()` reads the ancestor's
      // *current* state at the moment this entry is built, not the state
      // captured when the originating child first emitted. Logs travel
      // synchronously up the bubble path, so for routine debug/info
      // entries the gap is single-digit microseconds and nothing has
      // moved. For warn/error entries that take an async detour (e.g. a
      // child awaits a request, then logs, then a parent's state changes
      // before re-emit), reader beware: `entry.hierarchy.<ancestor>`
      // describes "right now," not "at emit-time."
      this.onEntry({
        timestamp: new Date().toISOString(),
        level: level as LogLevel,
        event,
        source: hierarchy[0]!.fullId,
        hierarchy: Object.fromEntries(hierarchy.map(h => [h.id, h.toJSON()])),
        ...(spanCtx && {
          traceId: spanCtx.traceId,
          spanId: spanCtx.spanId,
        }),
        ...(context &&
          Object.keys(context).length > 0 && {
            context: context as Record<string, unknown>,
          }),
      })
    }

    target.on('log', listener)
    this.listeners.set(target, listener)
  }

  /**
   * Stop observing `target`'s log events. Removes the listener registered
   * by a prior `attach(target)` call. No-op if `attach` was never called
   * for this target (or `detach` has already been called for it).
   *
   * @param target - The node previously passed to `attach()`.
   */
  detach(target: CoreClass): void {
    const listener = this.listeners.get(target)
    if (!listener) return
    target.off('log', listener)
    this.listeners.delete(target)
  }

  /**
   * Minimum log level numeric value. Events below this threshold are dropped
   * before `onEntry()` is called.
   *
   * Default reads `CoreClass.globalLogLevel` on every event so that
   * `instance.setLogLevel()` takes effect immediately without
   * requiring a restart. Override to pin to a fixed level from config.
   *
   * This is a *coarse* secondary gate. The primary per-instance level
   * filter happens *upstream* in the bubble path: every `CoreClass.adopt()`
   * link runs `shouldLog(level)` against the parent's effective level
   * before re-emitting, so a node with `setLogLevel('warn')` already
   * drops its `debug`/`info` events before they reach the sink. This
   * method only catches the cross-cutting case where the sink itself
   * wants to enforce a floor regardless of the tree's per-node levels —
   * e.g. an audit sink that ships `warn` and above only.
   */
  protected getMinLevel(): number {
    return CoreClass.globalLogLevel
  }

  /**
   * Called with a fully built `LogEntry` for each log event that passes the
   * level filter. Implement this to write to stdout, the browser console,
   * a remote endpoint, etc.
   *
   * @param entry - The structured log entry ready for output or shipping.
   */
  protected abstract onEntry(entry: LogEntry): void

  /**
   * Publicly render a pre-built `LogEntry` through this observer — applies
   * the level filter, then delegates to `onEntry()`. Useful for
   * republishing entries that arrived from outside the normal event bus
   * (e.g., an `EphemeralDaemon` parsing JSON lines from a subprocess's
   * stdout).
   *
   * @param entry - The structured log entry to render.
   */
  emit(entry: LogEntry): void {
    if (LOG_LEVEL_VALUES[entry.level] < this.getMinLevel()) return
    this.onEntry(entry)
  }
}

////////////////////////////////////////////////////////////////////////////////
// LogEntry type guard
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// BufferingLogObserver
////////////////////////////////////////////////////////////////////////////////

/**
 * `tail(...)` filter shape. All fields optional with AND semantics —
 * every supplied predicate must match for an entry to land in the
 * result set. The matched entries are returned newest-first, capped
 * by `limit`.
 *
 * @property limit - Max entries to return. Defaults to 200 when
 *   omitted; capped by the observer's buffer size.
 * @property minLevel - Lowest level to include (`'debug'` is the
 *   permissive floor; `'error'` returns only errors).
 * @property source - Substring match against `LogEntry.source`
 *   (case-sensitive). Use to scope to one daemon: `source: 'lab'`.
 * @property eventPrefix - Match against `LogEntry.event` by prefix.
 *   Useful for grouping a family of related events (`'onSignIn'` /
 *   `'onSignIn.success'` / `'onSignIn.failure'`).
 * @property sinceTimestamp - ISO timestamp inclusive lower bound.
 *   Use to scope to "events since this user action" — pair with the
 *   timestamp of a prior tool call.
 */
export type LogTailFilter = {
  limit?: number
  minLevel?: LogLevel
  source?: string
  eventPrefix?: string
  sinceTimestamp?: string
}

/**
 * In-memory ring-buffered log sink. Subclass-of-`LogObserver` that
 * captures every entry it receives into a fixed-size circular buffer.
 *
 * Designed for QA tooling: the MCP `harness.logs.*` family attaches
 * one to a running `LabcraftHarness` and exposes `tail` over the
 * wire so the agent can inspect what fired during a user-story
 * step.
 *
 * The observer is attached to the harness root via
 * `attach(harness)` (catches every in-process bubble event the way
 * `LogSink` does) AND receives subprocess-parsed `LogEntry` objects
 * directly via `emit(entry)` — `TestDaemon.addLogObserver(buffer)`
 * fans out each parsed line. Both paths converge on the same ring
 * buffer with the source/hierarchy preserved as the subprocess
 * emitted them.
 *
 * Memory bound: `capacity` entries (default 2000). Oldest entries
 * are evicted when full.
 */
export class BufferingLogObserver extends LogObserver {
  private readonly capacity: number
  private readonly buffer: LogEntry[] = []

  /**
   * @param capacity - Max entries to hold. Defaults to 2000 (about
   *   the size of a chatty integration-test run's emit stream).
   *   Bump for longer sessions; cap lower for memory-constrained
   *   environments.
   */
  constructor(capacity: number = 2000) {
    super()
    this.capacity = capacity
  }

  /**
   * Total entries currently in the buffer. May be less than
   * `capacity` while the buffer is filling; capped at `capacity`
   * once full.
   */
  get size(): number {
    return this.buffer.length
  }

  /** The configured buffer capacity. Read-only after construction. */
  get bufferCapacity(): number {
    return this.capacity
  }

  protected override onEntry(entry: LogEntry): void {
    this.buffer.push(entry)
    if (this.buffer.length > this.capacity) {
      // Drop oldest entry. `shift()` is O(n) but buffer is small
      // (default 2000) and writes are not on the hot path of any
      // production daemon — only the harness sees them.
      this.buffer.shift()
    }
  }

  /**
   * Return the matching tail of buffered entries, newest-first.
   * Filters apply AND semantics; absent filters don't constrain.
   * Default limit is 200; explicit limit is honoured up to the
   * buffer capacity.
   *
   * @param filter - Optional filter shape (see `LogTailFilter`).
   */
  tail(filter: LogTailFilter = {}): LogEntry[] {
    const limit = filter.limit ?? 200
    const minLevelValue =
      filter.minLevel !== undefined ? LOG_LEVEL_VALUES[filter.minLevel] : 0
    const result: LogEntry[] = []
    // Walk the buffer newest-first so we can stop at `limit` without
    // collecting everything first.
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const entry = this.buffer[i]!
      if (LOG_LEVEL_VALUES[entry.level] < minLevelValue) continue
      if (
        filter.source !== undefined &&
        !entry.source.includes(filter.source)
      ) {
        continue
      }
      if (
        filter.eventPrefix !== undefined &&
        !entry.event.startsWith(filter.eventPrefix)
      ) {
        continue
      }
      if (
        filter.sinceTimestamp !== undefined &&
        entry.timestamp < filter.sinceTimestamp
      ) {
        continue
      }
      result.push(entry)
      if (result.length >= limit) break
    }
    return result
  }

  /**
   * Drop every buffered entry. Useful between QA test cases so the
   * agent's `harness.logs.tail` doesn't see noise from prior steps.
   */
  clear(): void {
    this.buffer.length = 0
  }
}

////////////////////////////////////////////////////////////////////////////////
// LogEntry type guard
////////////////////////////////////////////////////////////////////////////////

/**
 * Narrow an unknown value to a `LogEntry`. Used by `EphemeralDaemon` to
 * detect which subprocess-stdout lines are log entries worth rendering
 * through `ConsoleLogSink` versus raw output to pass through.
 *
 * @param value - Candidate object (typically a `JSON.parse` result).
 * @returns True iff the value's shape matches `LogEntry`.
 */
export function isLogEntry(value: unknown): value is LogEntry {
  if (typeof value !== 'object' || value === null) return false
  const o = value as Record<string, unknown>
  if (typeof o['timestamp'] !== 'string') return false
  if (typeof o['event'] !== 'string') return false
  if (typeof o['source'] !== 'string') return false
  if (typeof o['hierarchy'] !== 'object' || o['hierarchy'] === null)
    return false
  if (typeof o['level'] !== 'string') return false
  if (!(o['level'] in LOG_LEVEL_VALUES)) return false
  return true
}

////////////////////////////////////////////////////////////////////////////////
// ConsoleLogSink
////////////////////////////////////////////////////////////////////////////////

/** ANSI color codes (ignored harmlessly in non-TTY environments) */
const COLORS = {
  reset: '\x1b[0m',
  dim: '\x1b[2m',
  bold: '\x1b[1m',
  cyan: '\x1b[36m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  red: '\x1b[31m',
} as const

/** Per-level color and symbol for TTY output */
const LEVEL_STYLES: Record<string, { color: string; symbol: string }> = {
  debug: { color: COLORS.dim, symbol: '·' },
  info: { color: COLORS.green, symbol: '✓' },
  warn: { color: COLORS.yellow, symbol: '⚠' },
  error: { color: COLORS.red, symbol: '✗' },
}

/** Detect Node.js TTY without throwing in browser/edge environments */
function detectTTY(): boolean {
  if (typeof process === 'undefined') return false
  return (process as NodeJS.Process).stdout?.isTTY ?? false
}

/** True when running in Node.js (not browser or edge worker) */
function isNodeProcess(): boolean {
  return typeof process !== 'undefined'
}

/**
 * Universal log sink that writes structured `LogEntry` objects to the
 * platform `console`.
 *
 * Output format is environment-aware:
 * - **Node.js TTY** — colorized multi-line:
 *   `HH:mm:ss.SSS ✓ info  event [traceId] @source`
 * - **Node.js non-TTY** — compact JSON for log aggregators
 * - **Browser** — `[LEVEL] event @source { context, state }`
 *   routed to `console.error/warn/debug/log` for native devtools colouring
 *
 * Level filtering is pinned to the level supplied at construction when
 * provided. Omit `minLevel` to follow `CoreClass.globalLogLevel` dynamically.
 */
export class ConsoleLogSink extends LogObserver {
  private readonly minLevelValue: number | undefined
  private readonly isTTY: boolean
  private readonly isNode: boolean

  /**
   * @param minLevel - Minimum log level to output. When provided, pinned at
   *   construction and does not track `instance.setLogLevel()`. When
   *   omitted, follows `CoreClass.globalLogLevel` dynamically.
   */
  constructor(minLevel?: LogLevel) {
    super()
    this.minLevelValue =
      minLevel !== undefined ? LOG_LEVEL_VALUES[minLevel] : undefined
    this.isNode = isNodeProcess()
    this.isTTY = detectTTY()
  }

  protected override getMinLevel(): number {
    return this.minLevelValue ?? CoreClass.globalLogLevel
  }

  protected override onEntry(entry: LogEntry): void {
    if (this.isTTY) {
      this.writeTTY(entry)
    } else if (this.isNode) {
      console.log(safeStringify(entry))
    } else {
      this.writeConsole(entry)
    }
  }

  /**
   * Colorized multi-line TTY output.
   * `HH:mm:ss.SSS ✓ info  event [traceId] @source`
   */
  private writeTTY(entry: LogEntry): void {
    const { color, symbol } = LEVEL_STYLES[entry.level] ?? {
      color: COLORS.reset,
      symbol: '?',
    }
    const time = entry.timestamp.slice(11, 23) // HH:mm:ss.SSS
    const lvl = entry.level.padEnd(5)

    let header =
      `${COLORS.dim}${time}${COLORS.reset} ` +
      `${color}${symbol} ${lvl}${COLORS.reset} ` +
      `${COLORS.bold}${entry.event}${COLORS.reset}`

    if (entry.traceId) {
      header += ` ${COLORS.dim}[${entry.traceId.slice(0, 8)}]${COLORS.reset}`
    }

    header += ` ${COLORS.cyan}@${entry.source}${COLORS.reset}`

    console.log(header)

    if (entry.context && Object.keys(entry.context).length > 0) {
      for (const [key, value] of Object.entries(entry.context)) {
        const formatted =
          typeof value === 'string' ? `"${value}"` : safeStringify(value)
        console.log(`  ${COLORS.dim}${key}:${COLORS.reset} ${formatted}`)
      }
    }

    // Service-state hierarchy is signal at warn/error (you want to
    // know what state every ancestor was in when the failure
    // happened) and noise at info/debug (every routine event would
    // dump the entire service tree). Suppress on the routine path.
    const showHierarchy = entry.level === 'warn' || entry.level === 'error'
    if (showHierarchy && Object.keys(entry.hierarchy).length > 0) {
      console.log(`  ${COLORS.dim}--- service state ---${COLORS.reset}`)
      for (const [id, state] of Object.entries(entry.hierarchy)) {
        console.log(
          `  ${COLORS.dim}${id}:${COLORS.reset} ${safeStringify(state)}`,
        )
      }
    }
  }

  /**
   * Structured single-line output for browser devtools.
   * `[LEVEL] event @source { context, state }`
   */
  private writeConsole(entry: LogEntry): void {
    const label = `[${entry.level.toUpperCase()}] ${entry.event} @${entry.source}`

    const detail: Record<string, unknown> = {}
    if (entry.context) detail['context'] = entry.context
    if (Object.keys(entry.hierarchy).length > 0) {
      detail['state'] = entry.hierarchy
    }

    const hasDetail = Object.keys(detail).length > 0

    if (entry.level === 'error') {
      hasDetail ? console.error(label, detail) : console.error(label)
    } else if (entry.level === 'warn') {
      hasDetail ? console.warn(label, detail) : console.warn(label)
    } else if (entry.level === 'debug') {
      hasDetail ? console.debug(label, detail) : console.debug(label)
    } else {
      hasDetail ? console.log(label, detail) : console.log(label)
    }
  }
}
